import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../axiosInstance';
import AuthContext from '../AuthContext';
import Swal from 'sweetalert2';

// ── Inline styles as a style tag injected via useEffect ──
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .er-login-root {
    min-height: 100vh;
    display: flex;
    background: #080c10;
    font-family: 'DM Sans', sans-serif;
    position: relative;
    overflow: hidden;
  }

  /* ── Animated background ── */
  .er-bg {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }
  .er-bg-glow1 {
    position: absolute;
    width: 600px; height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%);
    top: -200px; right: -100px;
    animation: erFloat1 8s ease-in-out infinite;
  }
  .er-bg-glow2 {
    position: absolute;
    width: 400px; height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%);
    bottom: -100px; left: -100px;
    animation: erFloat2 10s ease-in-out infinite;
  }
  .er-bg-grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(31,45,68,0.25) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(31,45,68,0.25) 1px, transparent 1px);
    background-size: 50px 50px;
  }
  @keyframes erFloat1 {
    0%,100% { transform: translate(0,0) scale(1); }
    50% { transform: translate(-30px, 30px) scale(1.1); }
  }
  @keyframes erFloat2 {
    0%,100% { transform: translate(0,0) scale(1); }
    50% { transform: translate(20px,-20px) scale(1.05); }
  }

  /* ── Left panel ── */
  .er-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 40px;
    position: relative;
    z-index: 1;
  }
  .er-brand {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 48px;
  }
  .er-logo-icon {
    width: 52px; height: 52px;
    background: linear-gradient(135deg, #f5a623, #e8881a);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px; color: #000;
    box-shadow: 0 8px 24px rgba(245,166,35,0.35);
  }
  .er-brand-name {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 32px;
    letter-spacing: 3px;
    color: #e8edf5;
  }
  .er-brand-name span { color: #f5a623; }

  .er-tagline {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(42px, 5vw, 72px);
    line-height: 0.95;
    letter-spacing: 2px;
    color: #e8edf5;
    text-align: center;
    margin-bottom: 24px;
  }
  .er-tagline .gold { color: #f5a623; }

  .er-desc {
    font-size: 16px;
    color: #7a8ba0;
    text-align: center;
    line-height: 1.7;
    max-width: 400px;
    margin-bottom: 48px;
  }

  .er-features {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 360px;
  }
  .er-feature {
    display: flex;
    align-items: center;
    gap: 14px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(31,45,68,0.8);
    border-radius: 12px;
    padding: 14px 18px;
    animation: erSlideIn 0.6s ease both;
  }
  .er-feature:nth-child(1) { animation-delay: 0.1s; }
  .er-feature:nth-child(2) { animation-delay: 0.2s; }
  .er-feature:nth-child(3) { animation-delay: 0.3s; }
  .er-feature:nth-child(4) { animation-delay: 0.4s; }
  @keyframes erSlideIn {
    from { opacity: 0; transform: translateX(-20px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .er-feature-icon {
    width: 36px; height: 36px;
    border-radius: 8px;
    background: rgba(245,166,35,0.1);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; flex-shrink: 0;
  }
  .er-feature-text { font-size: 13px; color: #c8d4e0; font-weight: 500; }

  /* ── Right panel (form) ── */
  .er-right {
    width: 460px;
    min-width: 460px;
    background: #0d1117;
    border-left: 1px solid #1f2d44;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 40px;
    position: relative;
    z-index: 1;
  }

  .er-form-wrap {
    width: 100%;
    animation: erFadeUp 0.5s ease both;
  }
  @keyframes erFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .er-form-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 36px;
    letter-spacing: 2px;
    color: #e8edf5;
    margin-bottom: 6px;
  }
  .er-form-sub {
    font-size: 14px;
    color: #7a8ba0;
    margin-bottom: 36px;
  }

  .er-field {
    margin-bottom: 20px;
  }
  .er-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #7a8ba0;
    margin-bottom: 8px;
  }
  .er-input-wrap {
    position: relative;
  }
  .er-input {
    width: 100%;
    background: #111827;
    border: 1px solid #1f2d44;
    border-radius: 10px;
    padding: 14px 48px 14px 44px;
    color: #e8edf5;
    font-size: 15px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .er-input:focus {
    border-color: #f5a623;
    box-shadow: 0 0 0 3px rgba(245,166,35,0.12);
  }
  .er-input::placeholder { color: #3d5068; }
  .er-input-icon {
    position: absolute;
    left: 14px; top: 50%;
    transform: translateY(-50%);
    color: #3d5068;
    font-size: 15px;
    pointer-events: none;
  }
  .er-input-toggle {
    position: absolute;
    right: 14px; top: 50%;
    transform: translateY(-50%);
    color: #3d5068;
    cursor: pointer;
    font-size: 14px;
    transition: color 0.2s;
    background: none;
    border: none;
    padding: 0;
  }
  .er-input-toggle:hover { color: #f5a623; }

  .er-error-box {
    background: rgba(255,71,87,0.1);
    border: 1px solid rgba(255,71,87,0.3);
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 13px;
    color: #ff4757;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .er-submit {
    width: 100%;
    padding: 15px;
    background: linear-gradient(135deg, #f5a623, #e8881a);
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 700;
    color: #000;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 8px;
    letter-spacing: 0.5px;
  }
  .er-submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(245,166,35,0.35);
  }
  .er-submit:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

  .er-spinner {
    width: 18px; height: 18px;
    border: 2px solid rgba(0,0,0,0.25);
    border-top-color: #000;
    border-radius: 50%;
    animation: erSpin 0.7s linear infinite;
  }
  @keyframes erSpin { to { transform: rotate(360deg); } }

  .er-divider {
    border: none;
    border-top: 1px solid #1f2d44;
    margin: 28px 0;
  }

  .er-footer-note {
    font-size: 12px;
    color: #3d5068;
    text-align: center;
    line-height: 1.6;
  }

  /* ── Force password change modal ── */
  .er-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(4px);
    z-index: 1040;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
  }
  .er-modal {
    background: #0d1117;
    border: 1px solid #1f2d44;
    border-radius: 16px;
    width: 100%;
    max-width: 480px;
    overflow: hidden;
    animation: erFadeUp 0.3s ease;
    max-height: 90vh;
    overflow-y: auto;
  }
  .er-modal-header {
    background: linear-gradient(135deg, rgba(245,166,35,0.15), rgba(232,136,26,0.08));
    border-bottom: 1px solid rgba(245,166,35,0.2);
    padding: 20px 24px;
    display: flex; align-items: center; gap: 12px;
  }
  .er-modal-header h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #e8edf5;
  }
  .er-modal-header .warn-icon {
    width: 36px; height: 36px;
    background: rgba(245,166,35,0.15);
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
  }
  .er-modal-body { padding: 24px; }
  .er-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #1f2d44;
    display: flex; justify-content: flex-end;
  }

  .er-req-list {
    background: rgba(255,255,255,0.02);
    border: 1px solid #1f2d44;
    border-radius: 10px;
    padding: 16px 20px;
    margin-top: 20px;
  }
  .er-req-list h6 {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #7a8ba0;
    margin-bottom: 12px;
  }
  .er-req-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    margin-bottom: 8px;
    transition: color 0.2s;
  }
  .er-req-item.met   { color: #00e676; }
  .er-req-item.unmet { color: #3d5068; }
  .er-req-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: background 0.2s;
  }
  .er-req-item.met   .er-req-dot { background: #00e676; }
  .er-req-item.unmet .er-req-dot { background: #1f2d44; }

  /* ── Mobile ── */
  @media (max-width: 768px) {
    .er-left { display: none; }
    .er-right {
      width: 100%;
      min-width: unset;
      border-left: none;
      padding: 40px 24px;
      background: #080c10;
    }
    .er-login-root { flex-direction: column; }
    /* Show brand on mobile inside form */
    .er-mobile-brand {
      display: flex !important;
      align-items: center;
      gap: 12px;
      justify-content: center;
      margin-bottom: 32px;
    }
  }
  @media (min-width: 769px) {
    .er-mobile-brand { display: none !important; }
  }
`;

function Login() {
  const [formData, setFormData]           = useState({ username: '', password: '' });
  const [showPassword, setShowPassword]   = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [showNewPassword, setShowNewPassword]         = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [tempUserData, setTempUserData]   = useState(null);
  const [passwordChangeData, setPasswordChangeData] = useState({ newPassword: '', confirmPassword: '' });

  const { login, company } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handlePasswordChangeInput = (e) =>
    setPasswordChangeData({ ...passwordChangeData, [e.target.name]: e.target.value });

  const validatePasswordStrength = (pw) => ({
    isValid: pw.length >= 8 &&
      /[a-z]/.test(pw) && /[A-Z]/.test(pw) &&
      /\d/.test(pw)   && /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pw),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        'https://apiemiride.socialpartners.in/api/auth/login',
        formData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = response.data;
      if (data.user.force_password_change) {
        setTempUserData(data);
        setShowForcePasswordChange(true);
        setLoading(false);
        return;
      }
      await handleSuccessfulLogin(data);
    } catch (err) {
      const message =
        err.response?.data?.error   ||
        err.response?.data?.message ||
        err.message                 ||
        'Invalid email or password.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = async (data) => {
    const accessToken  = data.token;
    const refreshToken = data.refreshToken;
    const userId       = data.user.id;
    const companyID    = data.user.company_id;

    localStorage.setItem('token',        accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('company_id',   companyID);

    const companyresponse = await axiosInstance.get('/billsettings/company');
    const companyData = companyresponse.data?.data?.[0] || {};

    const userData = {
      company_name:     companyData.company_name     || '',
      company_address1: companyData.company_address1 || '',
      company_address2: companyData.company_address2 || '',
      company_city:     companyData.company_city     || '',
      company_phone1:   companyData.company_phone1   || '',
      company_phone2:   companyData.company_phone2   || '',
      logo_filename:    companyData.logo_filename     || '',
    };

    const permissionRes = await axiosInstance.get(`/permission/user/${userId}`);
    const permissions   = permissionRes.data.data || [];

    login(
      data.user.id, data.user.company_id, accessToken, refreshToken,
      data.user.fullname, data.user.email, data.user.usertype, data.user.profile_picture
    );
    company(userData);
    localStorage.setItem('permissions', JSON.stringify(permissions));

    let redirectTo = '/emicalculator';
    if (permissions.includes('dashboard')) redirectTo = '/dashboard';

    Swal.fire({ icon: 'success', title: 'Welcome back!', timer: 1500, showConfirmButton: false });
    navigate(redirectTo);
  };

  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      Swal.fire({ icon: 'error', title: 'Mismatch', text: 'Passwords do not match!' });
      return;
    }
    if (!validatePasswordStrength(passwordChangeData.newPassword).isValid) {
      Swal.fire({ icon: 'error', title: 'Weak Password', text: 'Please meet all password requirements.' });
      return;
    }
    try {
      setLoading(true);
      const response = await axios.put(
        `https://apiemiride.socialpartners.in/api/user/forcepassword/${tempUserData.user.id}`,
        { password: passwordChangeData.newPassword },
        {
          headers: {
            Authorization: `Bearer ${tempUserData.accessToken}`,
            'x-company-id': tempUserData.user.company_id,
          },
        }
      );
      const loginData = {
        token: tempUserData.accessToken,
        refreshToken: tempUserData.refreshToken,
        user: {
          ...response.data.user,
          id:              tempUserData.user.id,
          company_id:      tempUserData.user.company_id,
          fullname:        tempUserData.user.fullname,
          email:           tempUserData.user.email,
          usertype:        tempUserData.user.usertype,
          profile_picture: tempUserData.user.profile_picture,
        },
      };
      await handleSuccessfulLogin(loginData);
      setShowForcePasswordChange(false);
      setTempUserData(null);
      setPasswordChangeData({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  // ── Password requirement checks ──
  const pw = passwordChangeData.newPassword;
  const reqs = [
    { label: 'At least 8 characters',        met: pw.length >= 8 },
    { label: 'One lowercase letter (a-z)',    met: /[a-z]/.test(pw) },
    { label: 'One uppercase letter (A-Z)',    met: /[A-Z]/.test(pw) },
    { label: 'One number (0-9)',              met: /\d/.test(pw) },
    { label: 'One special character (!@#…)',  met: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pw) },
    { label: 'Passwords match',               met: pw !== '' && pw === passwordChangeData.confirmPassword },
  ];

  return (
    <>
      <style>{styles}</style>

      <div className="er-login-root">

        {/* Background effects */}
        <div className="er-bg">
          <div className="er-bg-grid"></div>
          <div className="er-bg-glow1"></div>
          <div className="er-bg-glow2"></div>
        </div>

        {/* ── Left panel ── */}
        <div className="er-left">
          <div className="er-brand">
            <div className="er-logo-icon">ER</div>
            <span className="er-brand-name">EMI<span>RIDE</span></span>
          </div>

          <div className="er-tagline">
            FINANCE<br/>
            <span className="gold">MADE</span><br/>
            SIMPLE
          </div>

          <p className="er-desc">
            Complete two-wheeler finance management — loans, EMI tracking, exchange, billing, and collection.
          </p>

          <div className="er-features">
            {[
              { icon: '🏦', text: 'Full loan lifecycle management' },
              { icon: '📊', text: 'Real-time dashboard & analytics' },
              { icon: '🧾', text: 'Receipts, billing & delivery notes' },
              { icon: '🔍', text: 'Collection & buyer search' },
            ].map((f, i) => (
              <div className="er-feature" key={i}>
                <div className="er-feature-icon">{f.icon}</div>
                <span className="er-feature-text">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel (form) ── */}
        <div className="er-right">
          <div className="er-form-wrap">

            {/* Brand shown on mobile only */}
            <div className="er-mobile-brand" style={{ display: 'none' }}>
              <div className="er-logo-icon">ER</div>
              <span className="er-brand-name">EMI<span>RIDE</span></span>
            </div>

            <div className="er-form-title">SIGN IN</div>
            <p className="er-form-sub">Enter your credentials to access your account</p>

            {/* Error */}
            {error && (
              <div className="er-error-box">
                <i className="fa fa-exclamation-triangle"></i>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>

              {/* Email */}
              <div className="er-field">
                <label className="er-label">Email Address</label>
                <div className="er-input-wrap">
                  <i className="fa fa-envelope er-input-icon"></i>
                  <input
                    type="email"
                    name="username"
                    className="er-input"
                    placeholder="your@email.com"
                    value={formData.username}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="er-field">
                <label className="er-label">Password</label>
                <div className="er-input-wrap">
                  <i className="fa fa-lock er-input-icon"></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="er-input"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                  />
                  <button type="button" className="er-input-toggle"
                    onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <button type="submit" className="er-submit" disabled={loading}>
                {loading ? (
                  <><div className="er-spinner"></div> Signing in...</>
                ) : (
                  <><i className="fa fa-sign-in"></i> Sign In</>
                )}
              </button>
            </form>

            <hr className="er-divider" />

            <p className="er-footer-note">
              Having trouble? Contact{' '}
              <a href="mailto:support@emiride.in" style={{ color: '#f5a623', textDecoration: 'none' }}>
                support@emiride.in
              </a>
            </p>

          </div>
        </div>
      </div>

      {/* ── Force Password Change Modal ── */}
      {showForcePasswordChange && (
        <div className="er-modal-overlay">
          <div className="er-modal">
            <div className="er-modal-header">
              <div className="warn-icon">🔐</div>
              <div>
                <h4>Password Change Required</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#7a8ba0' }}>
                  Please set a new password to continue
                </p>
              </div>
            </div>

            <form onSubmit={handleForcePasswordChange}>
              <div className="er-modal-body">

                {/* New Password */}
                <div className="er-field">
                  <label className="er-label">New Password</label>
                  <div className="er-input-wrap">
                    <i className="fa fa-lock er-input-icon"></i>
                    <input type={showNewPassword ? 'text' : 'password'} name="newPassword"
                      className="er-input" placeholder="Create a strong password"
                      value={passwordChangeData.newPassword}
                      onChange={handlePasswordChangeInput} required />
                    <button type="button" className="er-input-toggle"
                      onClick={() => setShowNewPassword(p => !p)} tabIndex={-1}>
                      <i className={`fa ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="er-field">
                  <label className="er-label">Confirm New Password</label>
                  <div className="er-input-wrap">
                    <i className="fa fa-lock er-input-icon"></i>
                    <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword"
                      className="er-input" placeholder="Repeat your password"
                      value={passwordChangeData.confirmPassword}
                      onChange={handlePasswordChangeInput} required />
                    <button type="button" className="er-input-toggle"
                      onClick={() => setShowConfirmPassword(p => !p)} tabIndex={-1}>
                      <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                {/* Requirements */}
                <div className="er-req-list">
                  <h6>Password Requirements</h6>
                  {reqs.map((r, i) => (
                    <div key={i} className={`er-req-item ${r.met ? 'met' : 'unmet'}`}>
                      <div className="er-req-dot"></div>
                      {r.label}
                    </div>
                  ))}
                </div>

              </div>

              <div className="er-modal-footer">
                <button type="submit" className="er-submit" style={{ width: 'auto', padding: '13px 32px' }}
                  disabled={loading}>
                  {loading
                    ? <><div className="er-spinner"></div> Updating...</>
                    : <><i className="fa fa-check mr-2"></i> Change Password & Login</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Login;